import { Box, Flex } from '@mantine/core'

import { ACCOUNT_TYPES } from '@/types/accounts.types'
import type { PlaidAccountSubtypes } from '@/types/plaid.types'

import { AccountsList } from '@/modules/accounts/components/AccountsList/AccountsList'
import type { AccountShellProps } from '@/modules/accounts/components/AccountsShell/AccountShell.types'
import { AccountType } from '@/modules/accounts/components/AccountType/AccounType'

import { TransactionSyncNotice } from '@/components/@Shared/TransactionSyncNotice/TransactionSyncNotice'
import { AddAccount } from '@/components/AddAccount/AddAccount'

import { formatMoney } from '@/helpers/formatMoney.helper'
import { getCurrentBalanceType } from '@/helpers/getCurrentBalanceType.helper'

import classes from './AccountsShell.module.css'

export function AccountsShell({ accounts }: AccountShellProps) {
  const listedAccounts = new Set()

  const accountTypes = [...new Set(accounts.map(account => account.type))]
  const accountSubtypes = [...new Set(accounts.map(account => account.subtype))]

  const availableAccountTypes = ACCOUNT_TYPES.filter(
    accountType =>
      accountType.type.some(t => accountTypes.includes(t)) &&
      (accountType.subtype
        ? accountSubtypes.some(accountSubtype =>
            accountType?.subtype?.includes(
              accountSubtype as PlaidAccountSubtypes,
            ),
          )
        : true),
  )

  const getFilteredAccounts = (types: string[], subtypes?: string[]) => {
    const filteredAccounts = accounts.filter(
      account =>
        types.includes(account.type) &&
        (subtypes ? subtypes.includes(account.subtype) : true) &&
        !listedAccounts.has(account.plaidId),
    )

    filteredAccounts.forEach(account => {
      listedAccounts.add(account.plaidId)
    })

    return filteredAccounts
  }

  const getAccountsInfo = (types: string[], subtype?: string[]) => {
    const filteredAccounts = getFilteredAccounts(types, subtype)

    const balance = filteredAccounts.reduce(
      (acc, account) =>
        acc +
        account.balance[getCurrentBalanceType(account.type, account.subtype)],
      0,
    )

    const formattedBalance = formatMoney(
      balance,
      filteredAccounts[0]?.balance.currency,
    )

    return {
      filteredAccounts,
      formattedBalance,
    }
  }

  return (
    <>
      <TransactionSyncNotice mb={{ base: 24, md: 48 }} />
      <Flex className={classes.container} direction="column">
        <Flex className={classes.row}>
          <Box className={classes.leftColumn}>
            <AddAccount />
          </Box>
        </Flex>

        {availableAccountTypes.map(accountType => {
          const { filteredAccounts, formattedBalance } = getAccountsInfo(
            accountType.type,
            accountType.subtype,
          )

          if (filteredAccounts.length === 0) {
            return null
          }

          return (
            <Flex key={accountType.label} className={classes.row}>
              <AccountType
                accountType={accountType}
                formattedBalance={formattedBalance}
              />

              <AccountsList accounts={filteredAccounts} />
            </Flex>
          )
        })}
      </Flex>
    </>
  )
}
